import { Trans, useLingui } from "@lingui/react/macro";
import { UnstyledLink } from "@medoma/components/Link/Link";
import styles from "./Chat.module.scss";
import { Outlet, useLocation, useParams } from "react-router";
import clsx from "clsx";
import type { IEmployee } from "@medoma/models/shifts";
import Portrait from "@medoma/components/Portrait/Portrait";
import { Heading } from "@medoma/components/Heading/Heading";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { useScheduledShifts } from "@/api/Shifts";
import {
  useRoomNamesWithUnreadNotifications,
  useEmployeeUnreadNotificationCount,
} from "@medoma/chat/useUnreadNotificationCounts";
import { NotificationCircle } from "@medoma/components/NotificationCircle/NotificationCircle";
import { useEmployees } from "@/api/Employee";
import { Loading } from "@medoma/components/Loading/Loading";
import NoResults from "@/components/NoResults/NoResults";
import { startOfDay } from "date-fns";
import { useUserProfile } from "@/api/Users";
import {
  isEmployeeRoomName,
  extractEmployeeIdFromRoomName,
} from "@medoma/chat/roomAlias";
import { CHAT_ENVIRONMENT } from "@/Utils/EnvUtils";
import { useMemo } from "react";
import InputField from "@medoma/components/InputField/InputField";
import { InlineAlert } from "@medoma/components/InlineAlert/InlineAlert";
import { useForm } from "react-hook-form";

const useEmployeesWithUnreadNotifications = (
  allEmployees: IEmployee[] | undefined,
) => {
  const { organization } = useUserProfile();

  const roomNamesWithUnreadNotifications =
    useRoomNamesWithUnreadNotifications();
  const employeeIds = [
    ...new Set(
      roomNamesWithUnreadNotifications
        .filter((name) => isEmployeeRoomName(name, organization))
        .map((roomName) =>
          extractEmployeeIdFromRoomName(roomName, CHAT_ENVIRONMENT),
        )
        .filter((id): id is string => id !== null),
    ),
  ];
  return employeeIds
    .map((employeeId) =>
      allEmployees?.find((employee) => employee.id.toString() === employeeId),
    )
    .filter((maybeEmployee) => maybeEmployee !== undefined) as IEmployee[];
};

const sortOnFirstName = (
  a: Pick<IEmployee, "firstName">,
  b: Pick<IEmployee, "firstName">,
) => {
  return a.firstName.localeCompare(b.firstName);
};

const filterEmployeesByName = (
  employees: IEmployee[],
  searchTerm: string,
  filterIsApplied: boolean,
): IEmployee[] => {
  if (!filterIsApplied) {
    return employees;
  }
  const lowerSearchTerm = searchTerm.toLowerCase();
  return employees.filter(
    (employee) =>
      employee.firstName.toLowerCase().includes(lowerSearchTerm) ||
      employee.lastName.toLowerCase().includes(lowerSearchTerm),
  );
};

const ChatListItemLink = ({
  employee,
  size,
}: {
  employee: IEmployee;
  size?: "compact";
}) => {
  const { organization } = useUserProfile();
  const { employeeId: employeeIdFromParams } = useParams();
  const location = useLocation();
  const { id, imageURL, firstName, lastName, competence } = employee;
  const employeeId = id.toString();
  const isEmployeeIdInPath = location.pathname.endsWith(`/${employeeId}`);

  const unreadNotificationsCount = useEmployeeUnreadNotificationCount({
    employeeId,
    env: CHAT_ENVIRONMENT,
    organization,
  });
  const hasUnreadNotifications =
    unreadNotificationsCount !== undefined && unreadNotificationsCount > 0;

  return (
    <UnstyledLink to={employeeId}>
      <div
        className={clsx([
          styles.chatListItemLink,
          isEmployeeIdInPath && styles.selectedListItem,
          employeeId === employeeIdFromParams && styles.active,
        ])}
      >
        <div className={styles.chatListItemLinkContent}>
          <Portrait
            name={`${firstName} ${lastName}`}
            photoUrl={imageURL}
            size={size === "compact" ? "small" : "large"}
          />
          <div className={styles.details}>
            <Heading level="h2" size="h3">
              {firstName} {lastName}
            </Heading>
            <div className={styles.competenceAndUnread}>
              {competence !== null ? (
                <CompetenceChip competence={competence} state="neutral" />
              ) : null}
              <NotificationCircle
                isVisible={hasUnreadNotifications}
                number={unreadNotificationsCount}
                placement="right"
              />
            </div>
          </div>
        </div>
      </div>
    </UnstyledLink>
  );
};

const ChatView = () => {
  const { t } = useLingui();
  const today = startOfDay(new Date());
  const { register, reset, watch } = useForm<{
    employeeSearch: string;
  }>();
  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const searchTerm = watch("employeeSearch") || "";

  const filterIsApplied = Boolean(searchTerm.trim());

  const { data: allEmployees } = useEmployees();
  const { data: shifts } = useScheduledShifts(today, today);
  const employeesWithUnreadNotifications =
    useEmployeesWithUnreadNotifications(allEmployees);

  const ambulatingEmployees = useMemo(() => {
    if (!allEmployees || !shifts) {
      return [];
    }
    const now = new Date();
    return allEmployees
      .filter((employee) =>
        shifts.some(
          (shift) =>
            shift.startDateTime <= now &&
            shift.endDateTime >= now &&
            shift.employee?.id === employee.id,
        ),
      )
      .filter(
        (employee) => !employeesWithUnreadNotifications.includes(employee),
      )
      .sort(sortOnFirstName);
  }, [allEmployees, shifts, employeesWithUnreadNotifications]);

  const restOfEmployees = useMemo(() => {
    if (!allEmployees) {
      return [];
    }
    return allEmployees
      .filter(
        (employee) =>
          !ambulatingEmployees.some(
            (ambulatingEmployee) => ambulatingEmployee.id === employee.id,
          ),
      )
      .filter(
        (employee) => !employeesWithUnreadNotifications.includes(employee),
      )
      .sort(sortOnFirstName);
  }, [allEmployees, ambulatingEmployees, employeesWithUnreadNotifications]);

  // Apply search filter to all employee lists
  const filteredUnreadEmployees = useMemo(
    () =>
      filterEmployeesByName(
        employeesWithUnreadNotifications,
        searchTerm,
        filterIsApplied,
      ),
    [employeesWithUnreadNotifications, searchTerm, filterIsApplied],
  );

  const filteredAmbulatingEmployees = useMemo(
    () =>
      filterEmployeesByName(ambulatingEmployees, searchTerm, filterIsApplied),
    [ambulatingEmployees, searchTerm, filterIsApplied],
  );

  const filteredRestOfEmployees = useMemo(
    () => filterEmployeesByName(restOfEmployees, searchTerm, filterIsApplied),
    [restOfEmployees, searchTerm, filterIsApplied],
  );

  if (!allEmployees || !shifts) {
    return <Loading message={t`Hämtar anställda`} />;
  }

  return (
    <div className={styles.chat}>
      <div className={styles.chatLists}>
        <div className={styles.searchWrapper}>
          <InputField
            type="search"
            label={t`Sök anställd`}
            showOptionalLabel={false}
            outerWidth="full"
            {...register("employeeSearch")}
          />
        </div>
        {filterIsApplied && (
          <div className={styles.alertWrapper}>
            <InlineAlert
              type="info"
              title={t`Sökt på "${searchTerm}"`}
              placement="fill"
              actions={[
                {
                  label: t`Rensa sökning`,
                  onClick: () => reset({ employeeSearch: "" }),
                },
              ]}
            />
          </div>
        )}
        {/* Removes heading in order to only show employees matching the search term */}
        {filterIsApplied ? (
          <section>
            <ul className={styles.chatList}>
              {filteredUnreadEmployees.map((employee) => (
                <li key={employee.id}>
                  <ChatListItemLink employee={employee} />
                </li>
              ))}
              {filteredAmbulatingEmployees.map((employee) => (
                <li key={employee.id}>
                  <ChatListItemLink employee={employee} />
                </li>
              ))}
              {filteredRestOfEmployees.map((employee) => (
                <li key={employee.id}>
                  <ChatListItemLink employee={employee} size="compact" />
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <>
            <section>
              <div className={styles.header}>
                <Heading level="h2" margin="bottom">
                  <Trans>Olästa</Trans>
                </Heading>
              </div>
              <ul className={styles.chatList}>
                {filteredUnreadEmployees.length > 0 ? (
                  filteredUnreadEmployees.map((employee) => (
                    <li key={employee.id}>
                      <ChatListItemLink employee={employee} />
                    </li>
                  ))
                ) : (
                  <NoResults
                    message={t`Inga olästa meddelanden`}
                    padding={24}
                  />
                )}
              </ul>
            </section>
            <section>
              <div className={styles.header}>
                <Heading level="h2" margin="top-and-bottom">
                  <Trans>Ambulerande</Trans>
                </Heading>
              </div>
              <ul className={styles.chatList}>
                {filteredAmbulatingEmployees.map((employee) => (
                  <li key={employee.id}>
                    <ChatListItemLink employee={employee} />
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <div className={styles.header}>
                <Heading level="h2" margin="top-and-bottom">
                  <Trans>Övriga</Trans>
                </Heading>
              </div>
              <ul className={styles.chatList}>
                {filteredRestOfEmployees.map((employee) => (
                  <li key={employee.id}>
                    <ChatListItemLink employee={employee} size="compact" />
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
      <Outlet />
    </div>
  );
};

export default ChatView;
